import {API_ROUTE_CONTACTS, API_ROUTE_DAYS, API_ROUTE_PRICES} from 'common/constants';
import {useSWRClient, useSWRPost} from 'common/uswSWR';
import theme, {breakpoint} from 'common/theme';
import {
  ContactApiRequestBody,
  ContactApiResponse,
  DaysApiResponse,
  SanityBookingFormData,
  PricesApiResponse,
} from 'common/types';
import {useCalendar} from 'common/useCalendar';
import Button from 'components/button';
import {Col, Row} from 'components/grid';
import NumberInput from 'components/numberInput';
import SanityBlockContent from 'components/sanityBlockContent';
import TextInput from 'components/textInput';
import {useFormik} from 'formik';
import {FC, useState} from 'react';
import {Calendar, DayRange} from 'react-modern-calendar-datepicker';
import styled from 'styled-components';
import * as Yup from 'yup';
import TextareaInput from 'components/textareaInput';
import Loading from 'components/loading';
import PricePreview from 'components/pricePreview';
import {calcPrice} from 'common/calcPrice';

const Info = styled.div<{type: 'success' | 'error'}>`
  width: 100%;
  max-width: 100%;
  min-width: 100%;
  padding: ${({theme}) => theme.spacing(0.75)}px ${({theme}) => theme.spacing(0.75)}px;
  color: ${({theme, type}) => (type === 'success' ? theme.colors.blue : theme.colors.red)};
  font-size: ${({theme}) => theme.fontSize(0.75)}px;
  line-height: ${({theme}) => theme.fontSize(0.95)}px;
  font-weight: 500;
  border: none;
  text-transform: none;
  text-decoration: none;
  border: 1px solid ${({theme, type}) => (type === 'success' ? theme.colors.blue : theme.colors.red)};
  border-radius: 5px;
  transition: border 250ms ease-in-out;
`;

const Wrapper = styled.section<{calendarError?: boolean}>`
  width: 100%;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 ${({theme}) => theme.spacing(1)}px;
  margin-top: ${({theme}) => theme.spacing(2)}px;
  margin-bottom: ${({theme}) => theme.spacing(1)}px;
  ${breakpoint.size('sm')`
    padding: 0 ${({theme}) => theme.spacing(2)}px;
    margin-bottom: ${({theme}) => theme.spacing(2)}px;
  `}
  ${breakpoint.size('md')`
    margin-top: ${({theme}) => theme.spacing(3)}px;
    margin-bottom: ${({theme}) => theme.spacing(3)}px;
  `};
  & .calendar {
    ${({calendarError, theme}) =>
      calendarError &&
      `
      border-color: ${theme.colors.red}
  `};
  }
`;

const CalendarFooterError = styled.div`
  margin-top: ${({theme}) => theme.spacing(0.25)}px;
  font-size: ${({theme}) => theme.fontSize(0.65)}px;
  line-height: ${({theme}) => theme.fontSize(0.65)}px;
  color: ${({theme}) => theme.colors.red};
`;

const CalendarLabel = styled.label`
  width: 100%;
  font-family: ${({theme}) => theme.fonts.teko};
`;

const TextContent = styled.div`
  color: ${({theme}) => theme.colors.black};
  opacity: 0.5;
  font-size: ${({theme}) => theme.fontSize(0.75)}px;
  line-height: ${({theme}) => theme.fontSize(0.95)}px;
  font-weight: 500;
  margin-top: 1.75em;
  margin-bottom: 1.75em;
`;

const initialValues: ContactApiRequestBody = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  from: null,
  to: null,
  people: 4,
  message: '',
};

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('Notwendig'),
  lastName: Yup.string().required('Notwendig'),
  email: Yup.string()
    .email('ungültige Email')
    .required('Notwenig'),
  phone: Yup.string(),
  from: Yup.date().required('Notwenig'),
  to: Yup.date().required('Notwenig'),
  people: Yup.number().required('Notwenig'),
  message: Yup.string(),
});

const BookingForm: FC<SanityBookingFormData> = ({content, terms, days, prices}) => {
  const {dayToDate, dateToDay} = useCalendar();
  const [formStatus, setFormStatus] = useState<'sent' | 'error'>();
  const {data: daysData} = useSWRClient<DaysApiResponse>(API_ROUTE_DAYS, days, {revalidateOnFocus: false});
  const {data: pricesData} = useSWRClient<PricesApiResponse>(API_ROUTE_PRICES, prices, {revalidateOnFocus: false});
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, helper) => {
      helper.setSubmitting(true);
      const data = await useSWRPost<ContactApiRequestBody, ContactApiResponse>(API_ROUTE_CONTACTS, values);
      if (!data.success) {
        setFormStatus('error');
        return;
      }
      setFormStatus('sent');
      formik.setSubmitting(false);
    },
  });
  const onChangeDayRange = (range: DayRange) => {
    formik.setFieldTouched('from', true);
    formik.setFieldTouched('to', true);
    formik.setValues({
      ...formik.values,
      from: range.from ? dayToDate(range.from, 'start') : null,
      to: range.to ? dayToDate(range.to, 'end') : null,
    });
  };
  const {locale, getDisabledDays, minimumDate, maximumDate, endingYear, startingYear} = useCalendar();

  const hasError = (field: keyof ContactApiRequestBody): boolean =>
    Boolean(formik.touched[field] && formik.errors[field]);
  const calendarError = hasError('from') || hasError('to');

  return (
    <>
      {formik.isSubmitting && <Loading />}
      <form onSubmit={formik.handleSubmit} autoComplete='off'>
        <Wrapper calendarError={calendarError}>
          <Row>
            {content && (
              <Col size={1}>
                <SanityBlockContent blocks={content} />
              </Col>
            )}
            <Col size={{xs: 1, md: 1 / 2, lg: 2 / 5}}>
              <CalendarLabel>Zeitraum</CalendarLabel>
              <Calendar
                calendarClassName='calendar'
                colorPrimary={theme.colors.blue}
                colorPrimaryLight={theme.colors.lightblue}
                locale={locale}
                disabledDays={getDisabledDays(daysData?.days)}
                minimumDate={minimumDate}
                maximumDate={maximumDate}
                onChange={onChangeDayRange}
                value={{
                  from: formik.values.from ? dateToDay(formik.values.from) : null,
                  to: formik.values.to ? dateToDay(formik.values.to) : null,
                }}
                selectorEndingYear={endingYear}
                selectorStartingYear={startingYear}
              />
              {calendarError && <CalendarFooterError>gültigen Zeitraum auswählen</CalendarFooterError>}
              {pricesData && formik.values.from && formik.values.to && (
                <>
                  <br />
                  <CalendarLabel>Preisübersicht</CalendarLabel>
                  <PricePreview
                    from={formik.values.from}
                    to={formik.values.to}
                    price={calcPrice(
                      pricesData?.prices || [],
                      formik.values.from,
                      formik.values.to,
                      formik.values.people
                    )}
                  />
                </>
              )}
            </Col>
            <Col size={{xs: 1, md: 1 / 2, lg: 3 / 5}}>
              <Row>
                <Col size={{xs: 1, sm: 1 / 2, md: 1, lg: 1 / 2}}>
                  <TextInput
                    name='firstName'
                    label='Vorname'
                    type='text'
                    placeholder='Max'
                    autoComplete='off'
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    hasError={hasError('firstName')}
                    error={formik.errors.firstName}
                  />
                </Col>
                <Col size={{xs: 1, sm: 1 / 2, md: 1, lg: 1 / 2}}>
                  <TextInput
                    name='lastName'
                    label='Nachname'
                    type='text'
                    placeholder='Mustermann'
                    autoComplete='off'
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    hasError={hasError('lastName')}
                    error={formik.errors.lastName}
                  />
                </Col>
                <Col size={{xs: 1, sm: 1 / 2, md: 1, lg: 1 / 2}}>
                  <TextInput
                    name='email'
                    label='Email'
                    type='email'
                    placeholder='max.mustermann@email.at'
                    autoComplete='off'
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    hasError={hasError('email')}
                    error={formik.errors.email}
                  />
                </Col>
                <Col size={{xs: 1, sm: 1 / 2, md: 1, lg: 1 / 2}}>
                  <TextInput
                    name='phone'
                    label='Telefon'
                    type='text'
                    placeholder='+43 5512 1234567'
                    autoComplete='off'
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    hasError={hasError('phone')}
                    error={formik.errors.phone}
                  />
                </Col>
                <Col size={{xs: 1, sm: 1 / 2, md: 1, lg: 1 / 2}}>
                  <NumberInput
                    label='Personen'
                    min={4}
                    max={8}
                    value={formik.values.people}
                    onChange={value => {
                      formik.setFieldValue('people', value);
                      formik.setFieldTouched('people', true);
                    }}
                    hasError={hasError('people')}
                    error={formik.errors.people}
                  />
                </Col>
                <Col size={{xs: 1}}>
                  <TextareaInput
                    name='message'
                    label='Nachricht'
                    placeholder='Hallo, ich möchte die Ferienhütte im angegebenen Zeitraum gerne reservieren...'
                    autoComplete='off'
                    rows={4}
                    value={formik.values.message}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    hasError={hasError('message')}
                    error={formik.errors.message}
                  />
                </Col>
                {formStatus === 'sent' && (
                  <Col size={1}>
                    <Info type='success'>
                      Das Anfrageformular wurde erfolgreich versendet.
                      <br />
                      <br />
                      Wir melden uns bei dir.
                    </Info>
                  </Col>
                )}
                {formStatus === 'error' && (
                  <Col size={1}>
                    <Info type='error'>
                      Leider ist ein Fehler beim Versenden des Anfrageformulars aufgetreten.
                      <br />
                      <br />
                      Bitte versuche es erneut.
                    </Info>
                  </Col>
                )}
                {(!formStatus || formStatus === 'error') && (
                  <Col size={{xs: 1, sm: 1 / 2, md: 1, lg: 1 / 2}}>
                    <Button type='submit' rounded fullwidth disabled={formik.isSubmitting}>
                      Senden
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
            {terms && (
              <Col size={1}>
                <TextContent
                  dangerouslySetInnerHTML={{
                    __html: terms.replace(/(?:\r\n|\r|\n)/g, '<br/>'),
                  }}
                />
              </Col>
            )}
          </Row>
        </Wrapper>
      </form>
    </>
  );
};

export default BookingForm;
